import type { ReactNode } from 'react';
import { Box, Typography, Paper, type SxProps, type Theme } from '@glplus/glpweb-dsl';

/** Shared pieces of the vendor review queue's three tabs. */

export const PRIMARY_BTN: SxProps<Theme> = {
  backgroundColor: '#f06500',
  color: '#ffffff',
  '&:hover': { backgroundColor: '#d35400' },
};

export const MUTED = '#5b6477';
export const HEADING = '#343F5B';
export const BORDER = '#e2e8f0';

export function day(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function money(cents: number | null | undefined): string {
  return cents == null ? '—' : `$${(cents / 100).toFixed(2)}`;
}

export function Card({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <Paper sx={{ p: 2.5 }} data-testid={testId}>
      {children}
    </Paper>
  );
}

/** A small caption over a value. An empty value reads as a dash, never as a gap. */
export function Field({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  const empty = children == null || children === '' || children === false;
  return (
    <Box sx={{ mb: 1.25 }} data-testid={testId}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography component="div" sx={{ fontSize: 15, color: HEADING, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {empty ? '—' : children}
      </Typography>
    </Box>
  );
}

/** Two columns: what one side says beside what the other says. */
export function SideBySide({ leftTitle, rightTitle, left, right }: { leftTitle: string; rightTitle: string; left: ReactNode; right: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
      {[
        { title: leftTitle, body: left },
        { title: rightTitle, body: right },
      ].map((col) => (
        <Box key={col.title} sx={{ border: `1px solid ${BORDER}`, borderRadius: '8px', p: 2 }}>
          <Typography component="h3" sx={{ fontSize: 14, fontWeight: 700, color: HEADING, mb: 1.5 }}>
            {col.title}
          </Typography>
          {col.body}
        </Box>
      ))}
    </Box>
  );
}

export function Photo({ src, alt, size = 120 }: { src: string | null | undefined; alt: string; size?: number }) {
  if (!src) {
    return (
      <Box sx={{ width: size, height: size, borderRadius: '8px', border: `1px dashed ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: 12, color: MUTED }}>No photo</Typography>
      </Box>
    );
  }
  return <Box component="img" src={src} alt={alt} sx={{ width: size, height: size, objectFit: 'contain', borderRadius: '8px', border: `1px solid ${BORDER}`, display: 'block', backgroundColor: '#fff' }} />;
}

/** The row of actions under a card, and the form one of them opens. */
export function Actions({ children }: { children: ReactNode }) {
  return <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: `1px solid ${BORDER}` }}>{children}</Box>;
}

export function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mt: 2, p: 2, backgroundColor: '#F7F7F8', borderRadius: '8px' }}>
      <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: HEADING, mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}
